import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { IntlShape } from 'react-intl';
import type { POISubType } from '@alltrails/search/types/algoliaResultTypes';
import { FAQTrail } from '../../../types/locationDetailsPages/shared';
import { TrailFaqLink } from '../FaqLink';

// not all POI types are included in FAQs
export type PoiFaqType = Extract<
  POISubType,
  | 'archaeological_site'
  | 'bay'
  | 'beach'
  | 'beach_hut'
  | 'bird_hide'
  | 'campsite'
  | 'canal'
  | 'castle'
  | 'cave'
  | 'cliff'
  | 'dam'
  | 'dune'
  | 'fort'
  | 'geyser'
  | 'glacier'
  | 'gorge'
  | 'hot_spring'
  | 'hut'
  | 'lagoon'
  | 'lake'
  | 'monument'
  | 'peak'
  | 'picnic_site'
  | 'pond'
  | 'rapids'
  | 'river'
  | 'rock_shelter'
  | 'ruins'
  | 'spring'
  | 'stream'
  | 'sun_shelter'
  | 'viewpoint'
  | 'volcano'
  | 'waterfall'
  | 'weather_shelter'
  | 'wildlife_hide'
>;

export const getPoiTypeQuestion = (locationName: string, poiType: PoiFaqType, intl: IntlShape) => {
  return {
    archaeological_site: intl.formatMessage(
      { defaultMessage: 'Are there any trails with archaeological sites in {locationName}?' },
      { locationName }
    ),
    bay: intl.formatMessage({ defaultMessage: 'Are there any trails with bays in {locationName}?' }, { locationName }),
    beach: intl.formatMessage({ defaultMessage: 'Are there any trails with beaches in {locationName}?' }, { locationName }),
    beach_hut: intl.formatMessage({ defaultMessage: 'Are there any trails with beach huts or shelters in {locationName}?' }, { locationName }),
    bird_hide: intl.formatMessage({ defaultMessage: 'Are there any trails with bird hides or bird blinds in {locationName}?' }, { locationName }),
    campsite: intl.formatMessage({ defaultMessage: 'Are there any trails with campsites in {locationName}?' }, { locationName }),
    canal: intl.formatMessage({ defaultMessage: 'Are there any trails with canals in {locationName}?' }, { locationName }),
    castle: intl.formatMessage({ defaultMessage: 'Are there any trails with castles in {locationName}?' }, { locationName }),
    cave: intl.formatMessage({ defaultMessage: 'Are there any trails with caves in {locationName}?' }, { locationName }),
    cliff: intl.formatMessage({ defaultMessage: 'Are there any trails with cliffs in {locationName}?' }, { locationName }),
    dam: intl.formatMessage({ defaultMessage: 'Are there any trails with dams in {locationName}?' }, { locationName }),
    dune: intl.formatMessage({ defaultMessage: 'Are there any trails with dunes in {locationName}?' }, { locationName }),
    fort: intl.formatMessage({ defaultMessage: 'Are there any trails with forts in {locationName}?' }, { locationName }),
    geyser: intl.formatMessage({ defaultMessage: 'Are there any trails with geysers in {locationName}?' }, { locationName }),
    glacier: intl.formatMessage({ defaultMessage: 'Are there any trails with glaciers in {locationName}?' }, { locationName }),
    gorge: intl.formatMessage({ defaultMessage: 'Are there any trails with gorges in {locationName}?' }, { locationName }),
    hot_spring: intl.formatMessage({ defaultMessage: 'Are there any trails with hot springs in {locationName}?' }, { locationName }),
    hut: intl.formatMessage({ defaultMessage: 'Are there any trails with huts in {locationName}?' }, { locationName }),
    lagoon: intl.formatMessage({ defaultMessage: 'Are there any trails with lagoons in {locationName}?' }, { locationName }),
    lake: intl.formatMessage({ defaultMessage: 'Are there any trails with lakes in {locationName}?' }, { locationName }),
    monument: intl.formatMessage({ defaultMessage: 'Are there any trails with historical monuments in {locationName}?' }, { locationName }),
    peak: intl.formatMessage({ defaultMessage: 'Are there any trails with scenic mountain peak views in {locationName}?' }, { locationName }),
    picnic_site: intl.formatMessage({ defaultMessage: 'Are there any trails with picnic areas in {locationName}?' }, { locationName }),
    pond: intl.formatMessage({ defaultMessage: 'Are there any trails with ponds in {locationName}?' }, { locationName }),
    rapids: intl.formatMessage({ defaultMessage: 'Are there any trails with river rapids in {locationName}?' }, { locationName }),
    river: intl.formatMessage({ defaultMessage: 'Are there any trails with rivers in {locationName}?' }, { locationName }),
    rock_shelter: intl.formatMessage({ defaultMessage: 'Are there any trails with rock shelters in {locationName}?' }, { locationName }),
    ruins: intl.formatMessage({ defaultMessage: 'Are there any trails with historical ruins in {locationName}?' }, { locationName }),
    spring: intl.formatMessage({ defaultMessage: 'Are there any trails with springs in {locationName}?' }, { locationName }),
    stream: intl.formatMessage({ defaultMessage: 'Are there any trails with streams in {locationName}?' }, { locationName }),
    sun_shelter: intl.formatMessage({ defaultMessage: 'Are there any trails with sun shelters in {locationName}?' }, { locationName }),
    viewpoint: intl.formatMessage({ defaultMessage: 'Are there any trails with scenic viewpoints in {locationName}?' }, { locationName }),
    volcano: intl.formatMessage({ defaultMessage: 'Are there any trails with volcano viewpoints in {locationName}?' }, { locationName }),
    waterfall: intl.formatMessage({ defaultMessage: 'Are there any trails with waterfalls in {locationName}?' }, { locationName }),
    weather_shelter: intl.formatMessage({ defaultMessage: 'Are there any trails with weather shelters in {locationName}?' }, { locationName }),
    wildlife_hide: intl.formatMessage(
      { defaultMessage: 'Are there any trails with wilderness hides or viewing shelters in {locationName}?' },
      { locationName }
    )
  }[poiType];
};

const trailLinksList = (trails: FAQTrail[], languageRegionCode: LanguageRegionCode, intl: IntlShape) => {
  const trailFaqLinks = trails.map(trail => <TrailFaqLink key={trail.slug} languageRegionCode={languageRegionCode} trail={trail} />);

  if (trailFaqLinks.length === 0) return null;
  if (trailFaqLinks.length === 1) return trailFaqLinks[0];
  return intl.formatList(trailFaqLinks, { type: 'conjunction' });
};

type PoiTypeAnswerProps = {
  locationName: string;
  poiType: PoiFaqType;
  trailsCount: number;
  trails: FAQTrail[];
  languageRegionCode: LanguageRegionCode;
  intl: IntlShape;
};

export const PoiTypeAnswer = ({ locationName, poiType, trailsCount, trails, languageRegionCode, intl }: PoiTypeAnswerProps) => {
  const trailLinks = trailLinksList(trails, languageRegionCode, intl);
  if (!trailLinks) return null;

  return {
    archaeological_site: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with an archaeological site in {locationName}: {trailLinks}} other {are {trailsCount} trails with archaeological sites in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    bay: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a bay in {locationName}: {trailLinks}} other {are {trailsCount} trails with bays in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    beach: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a beach in {locationName}: {trailLinks}} other {are {trailsCount} trails with beaches in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    beach_hut: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a beach hut or shelter in {locationName}: {trailLinks}} other {are {trailsCount} trails with beach huts or shelters in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    bird_hide: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a bird hide or bird blind in {locationName}: {trailLinks}} other {are {trailsCount} trails with bird hides or bird blinds in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    campsite: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a campsite in {locationName}: {trailLinks}} other {are {trailsCount} trails with campsites in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    canal: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a canal in {locationName}: {trailLinks}} other {are {trailsCount} trails with canals in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    castle: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a castle in {locationName}: {trailLinks}} other {are {trailsCount} trails with castles in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    cave: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a cave in {locationName}: {trailLinks}} other {are {trailsCount} trails with caves in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    cliff: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a cliff in {locationName}: {trailLinks}} other {are {trailsCount} trails with cliffs in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    dam: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a dam in {locationName}: {trailLinks}} other {are {trailsCount} trails with dams in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    dune: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a dune in {locationName}: {trailLinks}} other {are {trailsCount} trails with dunes in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    fort: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a fort in {locationName}: {trailLinks}} other {are {trailsCount} trails with forts in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    geyser: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a geyser in {locationName}: {trailLinks}} other {are {trailsCount} trails with geysers in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    glacier: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a glacier in {locationName}: {trailLinks}} other {are {trailsCount} trails with glaciers in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    gorge: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a gorge in {locationName}: {trailLinks}} other {are {trailsCount} trails with gorges in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    hot_spring: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a hot spring in {locationName}: {trailLinks}} other {are {trailsCount} trails with hot springs in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    hut: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a hut in {locationName}: {trailLinks}} other {are {trailsCount} trails with huts in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    lagoon: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a lagoon in {locationName}: {trailLinks}} other {are {trailsCount} trails with lagoons in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    lake: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a lake in {locationName}: {trailLinks}} other {are {trailsCount} trails with lakes in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    monument: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a historical monument in {locationName}: {trailLinks}} other {are {trailsCount} trails with historical monuments in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    peak: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a scenic mountain peak view in {locationName}: {trailLinks}} other {are {trailsCount} trails with scenic mountain peak views in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    picnic_site: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a picnic area in {locationName}: {trailLinks}} other {are {trailsCount} trails with picnic areas in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    pond: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a pond in {locationName}: {trailLinks}} other {are {trailsCount} trails with ponds in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    rapids: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with river rapids in {locationName}: {trailLinks}} other {are {trailsCount} trails with river rapids in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    river: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a river in {locationName}: {trailLinks}} other {are {trailsCount} trails with rivers in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    rock_shelter: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a rock shelter in {locationName}: {trailLinks}} other {are {trailsCount} trails with rock shelters in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    ruins: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with historical ruins in {locationName}: {trailLinks}} other {are {trailsCount} trails with historical ruins in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    spring: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a spring in {locationName}: {trailLinks}} other {are {trailsCount} trails with springs in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    stream: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a stream in {locationName}: {trailLinks}} other {are {trailsCount} trails with streams in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    sun_shelter: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a sun shelter in {locationName}: {trailLinks}} other {are {trailsCount} trails with sun shelters in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    viewpoint: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a scenic viewpoint in {locationName}: {trailLinks}} other {are {trailsCount} trails with scenic viewpoints in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    volcano: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a volcano viewpoint in {locationName}: {trailLinks}} other {are {trailsCount} trails with volcano viewpoints in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    waterfall: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a waterfall in {locationName}: {trailLinks}} other {are {trailsCount} trails with waterfalls in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    weather_shelter: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a weather shelter in {locationName}: {trailLinks}} other {are {trailsCount} trails with weather shelters in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    ),
    wildlife_hide: intl.formatMessage(
      {
        defaultMessage:
          'Yes, there {trailsCount, plural, one {is one trail with a wilderness hide or viewing shelter in {locationName}: {trailLinks}} other {are {trailsCount} trails with wilderness hides or viewing shelters in {locationName}, including {trailLinks}}}.'
      },
      { trailsCount, locationName, trailLinks }
    )
  }[poiType];
};
